/* ──────────────────────────────────────────────────────────────
   screens/PriceReview/index.tsx · Live attention queue + price relink
   (sk-catalog-api GET /attention-queue, POST /items/:id/relink-price)
   ────────────────────────────────────────────────────────────── */

import React, { useState, useEffect, useCallback } from 'react';
import { useReloadOnFocus } from '../navigation/useReloadOnFocus';
import { View, Text, ScrollView, ActivityIndicator, ViewStyle } from 'react-native';

import { useTheme } from '../context/themeContext';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { AppStackParamList } from '../navigation/types';
import { Icon, Button, Chip, Empty, useToast } from '../components/ui';
import { CONTENT } from '../lib/responsive';
import { getAttentionQueue, relinkPrice } from '../services/catalog/bundle';
import { handleApiError } from '../utils/errorHandler';
import { showSuccess } from '../utils/successHandler';

export default function PriceReviewQueue() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows, font } = theme;
  const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();
  const toast = useToast();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAttentionQueue({ limit: 100 });
      setRows((res as any).items ?? (Array.isArray(res) ? res : []));
    } catch (err) {
      handleApiError(err, 'Could not load the attention queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useReloadOnFocus(load);

  const onRelink = async (id: string) => {
    try {
      await relinkPrice(id, { mode: 'recompute' });
      showSuccess({}, 'Price relinked', 'Rollup recomputed.');
      await load();
    } catch (err) {
      handleApiError(err, 'Could not relink price');
    }
  };

  const card: ViewStyle = { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r3, overflow: 'hidden', ...(shadows.sm as ViewStyle) };

  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.paper }} contentContainerStyle={{ padding: 24, paddingBottom: 64, maxWidth: CONTENT.default }}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <View style={{ flex: 1, minWidth: 240 }}>
          <Text style={{ fontSize: fontSize['3xl'], fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans, letterSpacing: -0.02 * fontSize['3xl'] }}>Price review</Text>
          <Text style={{ fontSize: fontSize.md, color: colors.ink3, fontFamily: font.sans, marginTop: 4 }}>
            Items flagged for attention — stale rollups, component price changes, overrides.
          </Text>
        </View>
        <Button size="sm" icon="refresh" onPress={load}>Refresh</Button>
      </View>

      <View style={card}>
        {loading ? (
          <View style={{ padding: 40, alignItems: 'center' }}><ActivityIndicator color={colors.accent} /></View>
        ) : rows.length === 0 ? (
          <View style={{ padding: 24 }}>
            <Empty icon={<Icon name="check" size={26} color={colors.ink4} />} title="Nothing needs attention">
              The attention queue is clear.
            </Empty>
          </View>
        ) : (
          rows.map((r, i) => {
            const id = r.id ?? r.itemId ?? r.catalogItemId;
            return (
              <View key={id || i} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, rowGap: 10, padding: 14, borderBottomWidth: i < rows.length - 1 ? 1 : 0, borderBottomColor: colors.line }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, minWidth: 160 }}>
                  <Icon name="alert-circle" size={16} color={colors.warn} />
                  <Text style={{ fontFamily: font.mono, fontSize: fontSize.sm, color: colors.ink }} numberOfLines={1}>
                    {r.skuOrReference || id?.slice?.(0, 8) || '—'}
                  </Text>
                  {(r.reason || r.attentionReason) && <Chip tone="warn">{r.reason || r.attentionReason}</Chip>}
                </View>
                <View style={{ flexDirection: 'row', gap: 6 }}>
                  {id && <Button size="sm" onPress={() => navigation.navigate('editor', { id })}>Open</Button>}
                  {id && <Button size="sm" kind="accent" icon="refresh" onPress={() => onRelink(id)}>Relink</Button>}
                </View>
              </View>
            );
          })
        )}
      </View>
    </ScrollView>
  );
}
